import React from 'react';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { DARK_OLIVE, STORM_BLUE } from '../../../theme/colors';

// A conic "comet" fading into transparent, masked to a ring and rotated — an
// orbiting wave. Two strokes: the Evora olive->storm gradient (default, for
// light surfaces) or `currentColor` (for coloured/gradient buttons, where it
// inherits the button text colour — usually white — and reads cleanly).
const GRADIENT_COMET = `conic-gradient(from 0deg,
  transparent 0deg, ${DARK_OLIVE} 110deg, ${STORM_BLUE} 300deg, transparent 360deg)`;
const INHERIT_COMET = `conic-gradient(from 0deg,
  transparent 0deg, currentColor 300deg, transparent 360deg)`;

const Ring = styled('span')({
  position: 'absolute',
  inset: 0,
  borderRadius: '50%',
  WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - var(--comet-ring)), #000 calc(100% - var(--comet-ring)))',
  mask: 'radial-gradient(farthest-side, transparent calc(100% - var(--comet-ring)), #000 calc(100% - var(--comet-ring)))',
  animation: 'cometOrbit 0.9s linear infinite',
  '@keyframes cometOrbit': { to: { transform: 'rotate(360deg)' } },
  '@media (prefers-reduced-motion: reduce)': { animation: 'cometOrbit 2.4s linear infinite' },
});

/**
 * On-colour loading spinner — a rotating gradient comet ring. Used inside
 * buttons and other coloured surfaces where the "spinning period" mark would
 * not read. Drop-in-shaped like MUI CircularProgress (size + sx).
 *
 * Props: size (px, default 20), stroke ('gradient' | 'inherit', default
 * 'gradient'). MUI-only props (color/thickness/variant/value/disableShrink)
 * are accepted and ignored.
 */
export default function CometSpinner({
  size = 20,
  stroke = 'gradient',
  sx,
  className,
  // swallow MUI CircularProgress props so they don't reach the DOM:
  color: _color, thickness: _thickness, variant: _variant, value: _value, disableShrink: _disableShrink,
  ...rest
}) {
  const px = typeof size === 'number' ? `${size}px` : size;
  const ringThickness = typeof size === 'number' ? Math.max(2, Math.round(size * 0.14)) : 3;
  return (
    <Box
      className={className}
      role="progressbar"
      aria-label="Loading"
      sx={{ position: 'relative', display: 'inline-flex', width: px, height: px, flexShrink: 0, ...sx }}
      style={{ '--comet-ring': `${ringThickness}px` }}
      {...rest}
    >
      <Ring style={{ background: stroke === 'inherit' ? INHERIT_COMET : GRADIENT_COMET }} />
    </Box>
  );
}
